/**
 * Signal Protocol Encryption Service
 * Main service for encrypting/decrypting messages using Signal Protocol
 */

import { MessagingService } from '../messagingService';
import { Message } from '../../types/database.types';
import { debug } from '../../../lib/utils/debug';
import nacl from 'tweetnacl';
import * as naclUtil from 'tweetnacl-util';

// Signal Protocol imports
import { SessionManager } from './sessionManager';
import { fetchPrekeyBundle } from './prekeyService';
import { encryptMessage as encryptRatchetMessage, decryptMessage as decryptRatchetMessage, decryptMessageWithSendingChain } from './doubleRatchet';
import { 
  encryptWithSenderKey, 
  decryptWithSenderKey,
  encryptSenderKeyForRecipient,
  decryptSenderKeyFromBlob
} from './senderKeys';
import { GroupSenderKeyManager, GroupSenderKey } from './groupSenderKeyManager';
import { supabase } from '../supabase';
import {
  ensureLibsignalV2Initialized,
  encryptDirectV2,
  decryptDirectV2,
  encryptGroupV2,
  decryptGroupV2,
  type SignalV2Envelope,
} from './libsignalV2';

/**
 * Identity Key Storage for Signal Protocol
 * Stores Ed25519 signing keys (identity keys)
 */
class SignalKeyStorage {
  private static readonly IDENTITY_KEY_STORAGE_PREFIX = 'signal_identity_key_';
  
  static storeIdentityKeyPair(userId: string, keyPair: nacl.SignKeyPair): void {
    const publicKey = naclUtil.encodeBase64(keyPair.publicKey);
    const secretKey = naclUtil.encodeBase64(keyPair.secretKey);
    localStorage.setItem(`${this.IDENTITY_KEY_STORAGE_PREFIX}_public_${userId}`, publicKey);
    localStorage.setItem(`${this.IDENTITY_KEY_STORAGE_PREFIX}_secret_${userId}`, secretKey);
  }
  
  static getIdentityKeyPair(userId: string): nacl.SignKeyPair | null {
    const publicKey = localStorage.getItem(`${this.IDENTITY_KEY_STORAGE_PREFIX}_public_${userId}`);
    const secretKey = localStorage.getItem(`${this.IDENTITY_KEY_STORAGE_PREFIX}_secret_${userId}`);
    
    if (!publicKey || !secretKey) {
      return null;
    }
    
    try {
      const decodedPublicKey = naclUtil.decodeBase64(publicKey);
      const decodedSecretKey = naclUtil.decodeBase64(secretKey);
      
      // Validate Ed25519 key sizes: public key should be 32 bytes, secret key should be 64 bytes
      if (decodedPublicKey.length !== 32 || decodedSecretKey.length !== 64) {
        debug.error(`Invalid key sizes detected: publicKey=${decodedPublicKey.length} bytes, secretKey=${decodedSecretKey.length} bytes. Expected: publicKey=32, secretKey=64. Clearing invalid keys.`);
        // Clear invalid keys (likely from old Curve25519 format)
        this.clearIdentityKeyPair(userId);
        // Also clear any old encryption service keys that might conflict
        try {
          localStorage.removeItem(`signal_identity_public_key_${userId}`);
          localStorage.removeItem(`signal_identity_secret_key_${userId}`);
        } catch (e) {
          // Ignore errors clearing old keys
        }
        return null;
      }
      
      // Additional validation: ensure secret key is actually an Ed25519 key
      // Ed25519 secret keys are 64 bytes: first 32 bytes are the seed, last 32 bytes are the public key
      // Verify the last 32 bytes match the public key
      const secretKeyPublicPart = decodedSecretKey.slice(32, 64);
      let keysMatch = true;
      if (secretKeyPublicPart.length !== decodedPublicKey.length) {
        keysMatch = false;
      } else {
        for (let i = 0; i < secretKeyPublicPart.length; i++) {
          if (secretKeyPublicPart[i] !== decodedPublicKey[i]) {
            keysMatch = false;
            break;
          }
        }
      }
      if (!keysMatch) {
        debug.error(`Ed25519 secret key validation failed: public key mismatch. Clearing invalid keys.`);
        this.clearIdentityKeyPair(userId);
        return null;
      }
      
      return {
        publicKey: decodedPublicKey,
        secretKey: decodedSecretKey
      };
    } catch (error) {
      debug.error('Error decoding identity key pair:', error);
      // Clear corrupted keys
      this.clearIdentityKeyPair(userId);
      return null;
    }
  }
  
  static hasIdentityKeyPair(userId: string): boolean {
    return !!localStorage.getItem(`${this.IDENTITY_KEY_STORAGE_PREFIX}_public_${userId}`);
  }
  
  static clearIdentityKeyPair(userId: string): void {
    localStorage.removeItem(`${this.IDENTITY_KEY_STORAGE_PREFIX}_public_${userId}`);
    localStorage.removeItem(`${this.IDENTITY_KEY_STORAGE_PREFIX}_secret_${userId}`);
  }
}

/**
 * Initialize Signal Protocol for a user
 * Generates Ed25519 identity key pair and prekey bundle
 */
export async function initializeSignalProtocol(userId: string): Promise<void> {
  try {
    // Best practical E2E for web: libsignal-protocol-typescript store + prekeys
    await ensureLibsignalV2Initialized(userId);
  } catch (error) {
    debug.error('Error initializing Signal Protocol:', error);
    throw error;
  }
}

/**
 * Upload prekey bundle (helper function)
 * @deprecated Not currently used - kept for future implementation
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function uploadPrekeyBundle(userId: string, identityKeyPair: nacl.SignKeyPair): Promise<void> {
  const { uploadPrekeyBundle } = await import('./prekeyService');
  await uploadPrekeyBundle(userId, identityKeyPair);
}

/**
 * Send encrypted message using Signal Protocol
 */
export async function sendSignalEncryptedMessage(
  senderId: string,
  recipientId: string,
  groupId: string,
  content: string,
  messageType: 'text' | 'image' | 'file' | 'announcement' = 'text',
  fileUrl?: string,
  fileName?: string,
  fileSize?: number,
  parentId?: string | null
): Promise<Message> {
  try {
    await ensureLibsignalV2Initialized(senderId);

    const isGroupMessage = recipientId === groupId;

    let envelope: SignalV2Envelope;
    if (isGroupMessage) {
      const { data: members, error } = await supabase
        .from('group_members')
        .select('user_id')
        .eq('group_id', groupId);

      if (error || !members) {
        throw new Error('Failed to fetch group members');
      }

      const memberIds = members.map(m => m.user_id);
      envelope = await encryptGroupV2(senderId, groupId, memberIds, content);
    } else {
      envelope = await encryptDirectV2(senderId, recipientId, content);
    }

    return await MessagingService.sendMessage(
      senderId,
      recipientId,
      groupId,
      `[SIGNAL]${JSON.stringify(envelope)}`,
      messageType,
      fileUrl,
      fileName,
      fileSize,
      parentId
    );
  } catch (error) {
    debug.error('Error sending message:', error);
    throw error;
  }
}

/**
 * Encrypt direct message using Double Ratchet
 * @deprecated Not currently used - kept for future implementation
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function encryptDirectMessage(
  senderId: string,
  recipientId: string,
  content: string,
  senderIdentityKeyPair: nacl.SignKeyPair
): Promise<string> {
  try {
    // Get or create session
    const remotePrekeyBundle = await fetchPrekeyBundle(recipientId);
    if (!remotePrekeyBundle) {
      throw new Error(`No prekey bundle found for recipient ${recipientId}`);
    }

    // Get or create session
    const session = await SessionManager.getOrCreateSession(
      senderId,
      recipientId,
      senderIdentityKeyPair,
      remotePrekeyBundle
    );

    // Encrypt message using Double Ratchet
    const { state: updatedState, message: encryptedMessage } = encryptRatchetMessage(
      session.state,
      content
    );

    // Update session
    SessionManager.updateSession(senderId, recipientId, updatedState);

    // Serialize encrypted message
    return JSON.stringify(encryptedMessage);
  } catch (error) {
    debug.error('Error encrypting direct message:', error);
    throw error;
  }
}

/**
 * Encrypt group message using Sender Keys Protocol
 * @deprecated Not currently used - kept for future implementation
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function encryptGroupMessage(
  senderId: string,
  groupId: string,
  content: string,
  senderIdentityKeyPair: nacl.SignKeyPair
): Promise<string> {
  try {
    // Get group members
    const { data: members, error } = await supabase
      .from('group_members')
      .select('user_id')
      .eq('group_id', groupId);

    if (error || !members) {
      throw new Error('Failed to fetch group members');
    }

    const memberIds = members.map(m => m.user_id).filter(id => id !== senderId);

    // Get or create sender key for this group
    // Convert Ed25519 secret key to Curve25519 for encryption
    // Use first 32 bytes of Ed25519 secret key as Curve25519 secret key
    const senderSecretKey = senderIdentityKeyPair.secretKey.slice(0, 32);
    
    const groupSenderKey = GroupSenderKeyManager.getOrCreateSenderKey(
      groupId,
      memberIds,
      senderId,
      senderSecretKey
    );

    // Encrypt message with sender key
    const { updatedKey, message: encryptedMessage } = encryptWithSenderKey(
      groupSenderKey.senderKey,
      content
    );

    // Update sender key
    GroupSenderKeyManager.updateSenderKey(groupId, senderId, updatedKey);

    // Encrypt sender key for each member (if not already encrypted)
    // In production, this would be done when members join
    // For now, we'll encrypt on-demand
    const encryptedSenderKeys: Record<string, string> = { ...groupSenderKey.encryptedForMembers };
    
    for (const memberId of memberIds) {
      if (!encryptedSenderKeys[memberId]) {
        // Fetch member's public key
        const { data: memberData } = await supabase
          .from('users')
          .select('public_key')
          .eq('id', memberId)
          .single();

        if (memberData?.public_key) {
          const memberPublicKey = naclUtil.decodeBase64(memberData.public_key);
          encryptedSenderKeys[memberId] = encryptSenderKeyForRecipient(
            updatedKey,
            memberPublicKey,
            senderSecretKey
          );
        }
      }
    }

    // Update group sender key with encrypted keys
    const updatedGroupKey = {
      ...groupSenderKey,
      senderKey: updatedKey,
      encryptedForMembers: encryptedSenderKeys
    };
    GroupSenderKeyManager.storeSenderKey(groupId, senderId, updatedGroupKey);

    // Serialize encrypted message with sender key info
    // Include encrypted sender keys for all members so they can decrypt
    // In production, this could be optimized to only include when needed
    return JSON.stringify({
      encryptedMessage,
      senderKeyId: updatedKey.keyId,
      senderId,
      encryptedSenderKeys: encryptedSenderKeys // Include all encrypted sender keys
    });
  } catch (error) {
    debug.error('Error encrypting group message:', error);
    throw error;
  }
}

/**
 * Decrypt received message using Signal Protocol
 */
export async function decryptSignalMessage(
  message: Message | { 
    content?: string | null; 
    sender_id?: string;
    recipient_id?: string; 
    group_id?: string;
    [key: string]: any;
  },
  userId: string
): Promise<string> {
  try {
    const content = message.content || '';
    
    if (!content) {
      return '';
    }

    // Check if message is Signal Protocol encrypted
    if (!content.startsWith('[SIGNAL]')) {
      // Not Signal Protocol encrypted, try legacy format
      if (content.startsWith('[E2E]')) {
        // Legacy encrypted message - decrypt using old method
        const { decryptReceivedMessage } = await import('../encryptedMessagingService');
        return await decryptReceivedMessage(message, userId);
      }
      // Not encrypted, return as-is
      return content;
    }

    // Extract encrypted data
    const encryptedDataStr = content.substring(8); // Remove '[SIGNAL]' prefix
    let encryptedData: any;
    
    try {
      encryptedData = JSON.parse(encryptedDataStr);
      
      // Validate encrypted data structure
      if (!encryptedData || typeof encryptedData !== 'object') {
        debug.error('Invalid encrypted data structure:', encryptedData);
        return '[Unable to decrypt - invalid format]';
      }
    } catch (parseError) {
      debug.error('Failed to parse Signal encrypted data:', parseError);
      return '[Unable to decrypt - invalid format]';
    }

    const isGroupMessage = message.group_id && message.sender_id && message.group_id === message.recipient_id;

    // libsignal v2 envelope (new, supported)
    if (encryptedData && encryptedData.v === 2 && encryptedData.protocol === 'libsignal') {
      if (encryptedData.kind === 'group') {
        return await decryptGroupV2(userId, message.group_id!, message.sender_id!, encryptedData as SignalV2Envelope);
      }
      return await decryptDirectV2(userId, message.sender_id!, encryptedData as SignalV2Envelope);
    }

    // Legacy messages (old custom implementations) are not supported reliably
    return isGroupMessage
      ? '[Unable to decrypt group message - legacy format. New messages should work.]'
      : '[Unable to decrypt message - legacy format. New messages should work.]';
  } catch (error: any) {
    const errorMessage = error?.message || String(error);
    const isGroupMessage = message.group_id && message.sender_id && message.group_id === message.recipient_id;
    
    // Log error but return user-friendly message
    debug.error('Error decrypting Signal message:', {
      error: errorMessage,
      messageId: message.id,
      senderId: message.sender_id,
      userId: userId,
      isGroupMessage
    });
    
    // Return a user-friendly error message instead of throwing
    if (isGroupMessage && errorMessage.includes('sender key')) {
      return '[Unable to decrypt group message - this may be an older message. Try signing out and back in to refresh keys; new messages should work.]';
    }
    return '[Unable to decrypt message - this may be an older message or your keys may have changed. Try signing out and back in; new messages should work.]';
  }
}

/**
 * Decrypt direct message using Double Ratchet
 * @deprecated Not currently used - kept for future implementation
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function decryptDirectMessage(
  userId: string,
  senderId: string,
  encryptedData: any
): Promise<string> {
  try {
    const userIdentityKeyPair = SignalKeyStorage.getIdentityKeyPair(userId);
    if (!userIdentityKeyPair) {
      throw new Error('User has no identity keys');
    }

    // Get session
    let session = SessionManager.getSession(userId, senderId);
    if (!session) {
      // Session doesn't exist - need to create one from sender's prekey bundle
      // This can happen if this is the first message from this sender
      debug.log(`No session found for ${userId} <-> ${senderId}, creating new session`);
      
      // Fetch sender's prekey bundle
      const senderPrekeyBundle = await fetchPrekeyBundle(senderId);
      if (!senderPrekeyBundle) {
        throw new Error(`No prekey bundle found for sender ${senderId}`);
      }
      
      // Create session
      session = await SessionManager.createSession(
        userId,
        senderId,
        userIdentityKeyPair,
        senderPrekeyBundle
      );
    }

    // Special handling for self-messages: Check if it's a simple encrypted self-message
    if (userId === senderId && encryptedData.type === 'self-message') {
      try {
        // Decrypt simple self-message
        const userIdentityKeyPair = SignalKeyStorage.getIdentityKeyPair(userId);
        if (!userIdentityKeyPair) {
          throw new Error('User has no identity keys');
        }
        const key = naclUtil.decodeBase64(naclUtil.encodeBase64(userIdentityKeyPair.secretKey.slice(0, 32)));
        const nonce = naclUtil.decodeBase64(encryptedData.nonce);
        const ciphertext = naclUtil.decodeBase64(encryptedData.ciphertext);
        const plaintextBytes = nacl.secretbox.open(ciphertext, nonce, key);
        
        if (!plaintextBytes) {
          throw new Error('Failed to decrypt self-message');
        }
        
        debug.log('Successfully decrypted self-message using simple encryption');
        return naclUtil.encodeUTF8(plaintextBytes);
      } catch (selfDecryptError: any) {
        debug.error('Failed to decrypt self-message:', selfDecryptError);
        throw selfDecryptError;
      }
    }
    
    // Special handling for self-messages: try sending chain first (for old format)
    if (userId === senderId && (!encryptedData.type || encryptedData.type !== 'self-message')) {
      try {
        // Try decrypting with sending chain (since we encrypted with sending chain)
        const { state: updatedState, plaintext } = decryptMessageWithSendingChain(
          session.state,
          encryptedData
        );
        
        // Update session
        SessionManager.updateSession(userId, senderId, updatedState);
        
        debug.log('Successfully decrypted self-message using sending chain');
        return plaintext;
      } catch (selfDecryptError: any) {
        debug.log('Failed to decrypt self-message with sending chain, trying receiving chain', {
          error: selfDecryptError?.message || String(selfDecryptError)
        });
        // Fall through to try receiving chain
      }
    }
    
    // Try to decrypt message with receiving chain (normal flow)
    try {
      const { state: updatedState, plaintext } = decryptRatchetMessage(
        session.state,
        encryptedData
      );

      // Update session
      SessionManager.updateSession(userId, senderId, updatedState);

      return plaintext;
    } catch (decryptError: any) {
      // Decryption failed - try to recover
      const errorMessage = decryptError?.message || String(decryptError);
      debug.error('Decryption failed, attempting recovery', {
        error: errorMessage,
        userId,
        senderId,
        messageHeader: encryptedData?.header,
        sessionExists: !!session,
        sessionCreatedAt: session?.createdAt
      });

      // Check if it's a session sync issue or decryption failure
      // Try resetting the session for any decryption error
      const shouldAttemptRecovery = 
        errorMessage.includes('old chain') || 
        errorMessage.includes('chain key') ||
        errorMessage.includes('Decryption failed') ||
        errorMessage.includes('corrupted') ||
        errorMessage.includes('not intended');
      
      if (shouldAttemptRecovery) {
        debug.log('Attempting to reset session and retry decryption', {
          userId,
          senderId,
          errorType: errorMessage
        });
        
        // Delete the current session
        try {
          SessionManager.deleteSession(userId, senderId);
          debug.log('Deleted existing session');
        } catch (deleteError) {
          debug.error('Error deleting session', deleteError);
        }
        
        // Try to create a new session from sender's prekey bundle
        try {
          const senderPrekeyBundle = await fetchPrekeyBundle(senderId);
          if (senderPrekeyBundle) {
            debug.log('Fetched sender prekey bundle, creating new session');
            const newSession = await SessionManager.createSession(
              userId,
              senderId,
              userIdentityKeyPair,
              senderPrekeyBundle
            );
            
            // Try decrypting again with the new session
            try {
              debug.log('Attempting decryption with new session');
              const { state: updatedState, plaintext } = decryptRatchetMessage(
                newSession.state,
                encryptedData
              );
              
              // Update session
              SessionManager.updateSession(userId, senderId, updatedState);
              
              debug.log('Successfully decrypted message after session reset');
              return plaintext;
            } catch (retryError: any) {
              debug.error('Decryption still failed after session reset', {
                error: retryError?.message || String(retryError),
                userId,
                senderId
              });
              // Message might be encrypted with a different key/session that no longer exists
              // This can happen if:
              // 1. Message was encrypted before Signal Protocol was initialized
              // 2. Session was lost/corrupted
              // 3. Message was encrypted with a different user's key
              throw new Error(`Unable to decrypt message - it may have been encrypted with a different session or key. Original error: ${errorMessage}`);
            }
          } else {
            debug.error('No prekey bundle found for sender during recovery', { senderId });
            throw new Error(`Unable to decrypt message - no prekey bundle found for sender. Original error: ${errorMessage}`);
          }
        } catch (recoveryError: any) {
          debug.error('Failed to recover session', {
            error: recoveryError?.message || String(recoveryError),
            userId,
            senderId
          });
          // Throw a more descriptive error
          throw new Error(`Unable to decrypt message - recovery failed. Original error: ${errorMessage}, Recovery error: ${recoveryError?.message || String(recoveryError)}`);
        }
      } else {
        // Not a recoverable error, throw as-is
        throw decryptError;
      }
    }
  } catch (error) {
    debug.error('Error decrypting direct message:', error);
    throw error;
  }
}

/**
 * Decrypt group message using Sender Keys Protocol
 * @deprecated Not currently used - kept for future implementation
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function decryptGroupMessage(
  userId: string,
  groupId: string,
  senderId: string,
  encryptedData: any
): Promise<string> {
  try {
    const userIdentityKeyPair = SignalKeyStorage.getIdentityKeyPair(userId);
    if (!userIdentityKeyPair) {
      throw new Error('User has no identity keys');
    }

    // Convert Ed25519 secret key to Curve25519 for encryption
    // Use first 32 bytes of Ed25519 secret key as Curve25519 secret key
    const userSecretKey = userIdentityKeyPair.secretKey.slice(0, 32);

    // Special case: If user is decrypting their own message, get sender key directly
    if (userId === senderId) {
      const groupKey = GroupSenderKeyManager.getSenderKey(groupId, senderId);
      if (groupKey && groupKey.senderKey) {
        try {
          // Decrypt message using the sender key we stored when encrypting
          // Note: The sender key's messageCount may have changed, but the key itself is the same
          // We need to use the key that matches the senderKeyId in the encrypted data
          const messageKeyId = encryptedData.encryptedMessage?.keyId || encryptedData.senderKeyId;
          
          // Check if the keyId matches - if not, the sender key might have been rotated
          if (groupKey.senderKey.keyId === messageKeyId) {
            const decryptedMessage = decryptWithSenderKey(groupKey.senderKey, encryptedData.encryptedMessage);
            debug.log(`Successfully decrypted own group message using stored sender key`);
            return decryptedMessage;
          } else {
            debug.warn(`Sender key ID mismatch for own message - key may have been rotated`, {
              storedKeyId: groupKey.senderKey.keyId,
              messageKeyId: messageKeyId
            });
            // Fall through to try encrypted blob approach
          }
        } catch (decryptError) {
          debug.error('Error decrypting own message with stored sender key:', decryptError);
          // Fall through to try encrypted blob approach
        }
      } else {
        debug.warn(`No stored sender key found for own message (groupId: ${groupId}, senderId: ${senderId})`);
      }
      // If we don't have the sender key stored or decryption failed, fall through to normal decryption flow
    }

    // Get sender's public key
    const { data: senderData } = await supabase
      .from('users')
      .select('public_key')
      .eq('id', senderId)
      .single();

    if (!senderData?.public_key) {
      throw new Error('Sender has no public key');
    }

    const senderPublicKey = naclUtil.decodeBase64(senderData.public_key);

    // Get sender key for this group
    const senderKey = await GroupSenderKeyManager.getSenderKeyForRecipient(
      groupId,
      senderId,
      userId,
      userSecretKey,
      senderPublicKey
    );

    if (!senderKey) {
      // Try to decrypt from encrypted blob in message
      // Check for both singular (legacy) and plural (new) field names
      const encryptedBlob = encryptedData.encryptedSenderKey || 
                           (encryptedData.encryptedSenderKeys && encryptedData.encryptedSenderKeys[userId]);
      
      if (encryptedBlob) {
        try {
          debug.log('Attempting to decrypt sender key from blob', {
            userId,
            senderId,
            groupId,
            hasEncryptedBlob: !!encryptedBlob,
            blobLength: encryptedBlob.length
          });
          
          const decryptedSenderKey = decryptSenderKeyFromBlob(
            encryptedBlob,
            userSecretKey,
            senderPublicKey
          );
          
          debug.log('Successfully decrypted sender key from blob');
          
          // Store the decrypted sender key for future use
          const groupKey: GroupSenderKey = {
            groupId,
            senderKey: decryptedSenderKey,
            encryptedForMembers: { [userId]: encryptedBlob },
            createdAt: Date.now(),
            lastRotated: Date.now()
          };
          GroupSenderKeyManager.storeSenderKey(groupId, senderId, groupKey);
          
          // Decrypt message
          return decryptWithSenderKey(decryptedSenderKey, encryptedData.encryptedMessage);
        } catch (decryptError: any) {
          const errorMessage = decryptError?.message || String(decryptError);
          const blobFormat = encryptedBlob?.includes(':') ? 'new' : 'old';
          
          // Log as warning instead of error since this is expected for old messages
          // Old messages encrypted without nonce cannot be decrypted - this is expected behavior
          debug.warn('Unable to decrypt sender key from blob (expected for old messages)', {
            error: errorMessage,
            userId,
            senderId,
            groupId,
            hasEncryptedBlob: !!encryptedBlob,
            blobFormat,
            blobLength: encryptedBlob?.length,
            note: 'This is expected for messages encrypted before the nonce fix'
          });
          
          // If decryption fails, the message might be:
          // 1. Encrypted with old format (random nonce but not stored) - can't decrypt
          // 2. Encrypted with wrong keys
          // 3. Corrupted data
          // 
          // Unfortunately, we can't recover from this. Return a user-friendly error message
          // instead of throwing, so other messages can still be displayed
          throw new Error(`Unable to decrypt group message - the sender key cannot be decrypted. This may happen with older messages encrypted before a security update. Error: ${errorMessage}`);
        }
      }
      
      // For self-messages, if we don't have the sender key, try to use the stored one
      // even if the keyId doesn't match (might be a rotation issue)
      if (userId === senderId) {
        const storedGroupKey = GroupSenderKeyManager.getSenderKey(groupId, senderId);
        if (storedGroupKey && storedGroupKey.senderKey) {
          try {
            // Try decrypting with the stored sender key even if keyId doesn't match
            // This handles cases where the key was rotated but we still have the old key
            const decryptedMessage = decryptWithSenderKey(storedGroupKey.senderKey, encryptedData.encryptedMessage);
            debug.log(`Successfully decrypted own group message using stored sender key (ignoring keyId mismatch)`);
            return decryptedMessage;
          } catch (decryptError) {
            // If this fails, fall through to error
            debug.warn('Failed to decrypt own message with stored sender key (ignoring keyId)', {
              error: decryptError
            });
          }
        }
      }
      
      // Debug: Log what we have in encryptedData
      // Log as warning since this might be expected for old messages
      debug.warn('No sender key found for group message', {
        hasEncryptedSenderKey: !!encryptedData.encryptedSenderKey,
        hasEncryptedSenderKeys: !!encryptedData.encryptedSenderKeys,
        encryptedSenderKeysKeys: encryptedData.encryptedSenderKeys ? Object.keys(encryptedData.encryptedSenderKeys) : [],
        userId,
        senderId,
        groupId,
        senderKeyId: encryptedData.senderKeyId,
        note: 'This may be expected for older messages'
      });
      
      throw new Error('No sender key found for group message');
    }

          // Decrypt message with stored sender key
    try {
      return decryptWithSenderKey(senderKey, encryptedData.encryptedMessage);
    } catch (decryptError: any) {
      // Log as warning - this might be expected for old messages
      debug.warn('Error decrypting group message with sender key', {
        error: decryptError?.message || String(decryptError),
        userId,
        senderId,
        groupId,
        senderKeyId: senderKey.keyId,
        messageKeyId: encryptedData.encryptedMessage?.keyId
      });
      throw decryptError;
    }
  } catch (error) {
    debug.error('Error decrypting group message:', error);
    throw error;
  }
}

